import { useState } from "react";
import { Wallet, BadgeDollarSign, ChevronRight, Loader2 } from "lucide-react";
import axios, { API } from "@/lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const TIERS = ["free", "pro", "truth", "agency", "sovereign_defense"];

export const DirectorsOverridePanel = ({ overrides, onOverrideCreated }) => {
  const [creditEmail, setCreditEmail] = useState("");
  const [creditDelta, setCreditDelta] = useState("");
  const [creditReason, setCreditReason] = useState("");
  const [tierAuditId, setTierAuditId] = useState("");
  const [tierValue, setTierValue] = useState("pro");
  const [tierReason, setTierReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submitCredit = async () => {
    if (!creditEmail.trim() || !creditDelta || !creditReason.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/overrides/credit`, {
        user_email: creditEmail.trim(),
        delta: parseFloat(creditDelta),
        reason: creditReason.trim(),
      });
      toast.success(`${data.client_email} balance is now $${data.new_balance.toFixed(2)}.`);
      setCreditEmail("");
      setCreditDelta("");
      setCreditReason("");
      onOverrideCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply credit override.");
    } finally {
      setBusy(false);
    }
  };

  const submitTier = async () => {
    if (!tierAuditId.trim() || !tierReason.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/overrides/tier`, {
        audit_id: tierAuditId.trim(),
        new_tier: tierValue,
        reason: tierReason.trim(),
      });
      toast.success(`Audit ${data.audit_id} comped to ${data.new_tier}.`);
      setTierAuditId("");
      setTierReason("");
      onOverrideCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply tier override.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6" data-testid="directors-override-panel">
      <div className="mb-1 flex items-center gap-2">
        <Wallet className="h-4 w-4 text-[#D4AF37]" />
        <h2 className="font-heading text-xl font-bold text-white">Director's Balance Override</h2>
      </div>
      <p className="mb-4 text-xs text-slate-500">Manually control client capital &mdash; goodwill credit or a direct tier comp, bypassing Stripe. Every action is logged below.</p>

      <div className="mb-4 grid gap-4 sm:grid-cols-2">
        <div className="rounded-md border border-surface-border bg-obsidian p-4" data-testid="override-credit-form">
          <p className="mb-2 flex items-center gap-1 text-sm font-semibold text-white"><BadgeDollarSign className="h-3.5 w-3.5" /> Credit Balance</p>
          <Input value={creditEmail} onChange={(e) => setCreditEmail(e.target.value)} placeholder="client@email.com" className="mb-2" data-testid="override-credit-email-input" />
          <Input value={creditDelta} onChange={(e) => setCreditDelta(e.target.value)} placeholder="Delta e.g. 50 or -25" type="number" className="mb-2" data-testid="override-credit-delta-input" />
          <Input value={creditReason} onChange={(e) => setCreditReason(e.target.value)} placeholder="Reason (required)" className="mb-2" data-testid="override-credit-reason-input" />
          <Button size="sm" disabled={busy} onClick={submitCredit} className="w-full gap-1 bg-[#D4AF37] text-obsidian hover:bg-[#D4AF37]/90" data-testid="override-credit-submit-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronRight className="h-3.5 w-3.5" />} Apply Credit
          </Button>
        </div>
        <div className="rounded-md border border-surface-border bg-obsidian p-4" data-testid="override-tier-form">
          <p className="mb-2 text-sm font-semibold text-white">Comp Tier (per audit)</p>
          <Input value={tierAuditId} onChange={(e) => setTierAuditId(e.target.value)} placeholder="Audit ID" className="mb-2" data-testid="override-tier-audit-input" />
          <select value={tierValue} onChange={(e) => setTierValue(e.target.value)} className="mb-2 w-full rounded-md border border-surface-border bg-obsidian px-3 py-2 text-sm text-white" data-testid="override-tier-select">
            {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Input value={tierReason} onChange={(e) => setTierReason(e.target.value)} placeholder="Reason (required)" className="mb-2" data-testid="override-tier-reason-input" />
          <Button size="sm" disabled={busy} onClick={submitTier} className="w-full gap-1 bg-[#D4AF37] text-obsidian hover:bg-[#D4AF37]/90" data-testid="override-tier-submit-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronRight className="h-3.5 w-3.5" />} Apply Tier Comp
          </Button>
        </div>
      </div>

      {overrides.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="overrides-log-empty">No overrides issued yet.</p>
      ) : (
        <div className="space-y-2" data-testid="overrides-log-list">
          {overrides.map((o) => (
            <div key={o.id} className="rounded-md border border-surface-border bg-obsidian p-3 text-xs text-slate-400" data-testid={`override-log-row-${o.id}`}>
              <span className="font-semibold text-white">{o.admin_email}</span>{" "}
              {o.override_type === "credit"
                ? <>adjusted <span className="text-truth-green">{o.client_email}</span> by ${o.delta} (new balance ${o.new_balance}) &mdash; {o.reason}</>
                : <>comped audit <span className="text-truth-green">{o.audit_id}</span> ({o.brand_name}) to <span className="text-[#D4AF37]">{o.new_tier}</span> &mdash; {o.reason}</>}
              <span className="ml-2 text-slate-600">{o.created_at ? new Date(o.created_at).toLocaleString() : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
